/**
 * Remap selected track indices after subtitleOptimize reinjects type-2/3 rows.
 * Prefer keeping the same track identity; when a type-2/3 row disappears, fall
 * back to the plain same-language track (never force a full preferred re-pick
 * while the prior plain/type-1 selection is still valid).
 */

export type YoutubeOptimizeSnap = {
  languageCode?: string;
  enhanced?: boolean;
  creatorEnhanced?: boolean;
  whisper?: boolean;
  restored?: boolean;
  kind?: string;
  vssId?: string;
};

export type NetflixOptimizeSnap = {
  language?: string;
  id?: string;
  enhanced?: boolean;
  optimizeSourceId?: string;
};

export type PrimeOptimizeSnap = {
  languageCode?: string;
  enhanced?: boolean;
  optimizeSourceIndex?: number;
};

export type CourseraOptimizeSnap = {
  code?: string;
  enhanced?: boolean;
  optimizeSourceCode?: string;
};

function sameYoutubeIdentity(
  track: YoutubeOptimizeSnap,
  snap: YoutubeOptimizeSnap,
): boolean {
  return (
    (track.languageCode ?? '') === (snap.languageCode ?? '')
    && Boolean(track.enhanced) === Boolean(snap.enhanced)
    && Boolean(track.creatorEnhanced) === Boolean(snap.creatorEnhanced)
    && Boolean(track.whisper) === Boolean(snap.whisper)
    && Boolean(track.restored) === Boolean(snap.restored)
    && (track.kind ?? '') === (snap.kind ?? '')
  );
}

export function snapYoutubeCaptionTrack(
  track: YoutubeOptimizeSnap | null | undefined,
): YoutubeOptimizeSnap | null {
  if (!track) return null;
  return {
    languageCode: track.languageCode,
    enhanced: track.enhanced,
    creatorEnhanced: track.creatorEnhanced,
    whisper: track.whisper,
    restored: track.restored,
    kind: track.kind,
    vssId: track.vssId,
  };
}

export function resolveYoutubeIndexAfterOptimizeToggle(
  tracks: YoutubeOptimizeSnap[],
  snap: YoutubeOptimizeSnap | null | undefined,
): number | undefined {
  if (!snap) return undefined;

  const exact = tracks.findIndex((t) => sameYoutubeIdentity(t, snap));
  if (exact >= 0) return exact;

  // Type 2 removed → plain same language (prefer creator over ASR).
  if (snap.creatorEnhanced && snap.languageCode) {
    let asr: number | undefined;
    for (let i = 0; i < tracks.length; i += 1) {
      const t = tracks[i];
      if (!t || t.languageCode !== snap.languageCode) continue;
      if (t.enhanced || t.creatorEnhanced || t.whisper || t.restored) continue;
      if (t.kind !== 'asr') return i;
      if (asr === undefined) asr = i;
    }
    return asr;
  }

  return undefined;
}

export function snapNetflixTextTrack(
  track: NetflixOptimizeSnap | null | undefined,
): NetflixOptimizeSnap | null {
  if (!track) return null;
  return {
    language: track.language,
    id: track.id,
    enhanced: track.enhanced,
    optimizeSourceId: track.optimizeSourceId,
  };
}

export function resolveNetflixIndexAfterOptimizeToggle(
  tracks: NetflixOptimizeSnap[],
  snap: NetflixOptimizeSnap | null | undefined,
): number | undefined {
  if (!snap) return undefined;

  if (snap.id) {
    const byId = tracks.findIndex((t) => t.id === snap.id);
    if (byId >= 0) return byId;
  }

  const exact = tracks.findIndex(
    (t) =>
      (t.language ?? '') === (snap.language ?? '')
      && Boolean(t.enhanced) === Boolean(snap.enhanced)
      && (t.id ?? '') === (snap.id ?? ''),
  );
  if (exact >= 0) return exact;

  // Type 3 Optimized removed → plain same language (or optimizeSourceId).
  if (snap.enhanced) {
    if (snap.optimizeSourceId) {
      const src = tracks.findIndex(
        (t) => !t.enhanced && t.id === snap.optimizeSourceId,
      );
      if (src >= 0) return src;
    }
    if (snap.language) {
      const plain = tracks.findIndex(
        (t) => !t.enhanced && t.language === snap.language,
      );
      if (plain >= 0) return plain;
    }
  }

  return undefined;
}

export function snapPrimeSubtitleUrl(
  track: PrimeOptimizeSnap | null | undefined,
): PrimeOptimizeSnap | null {
  if (!track) return null;
  return {
    languageCode: track.languageCode,
    enhanced: track.enhanced,
    optimizeSourceIndex: track.optimizeSourceIndex,
  };
}

export function resolvePrimeIndexAfterOptimizeToggle(
  tracks: PrimeOptimizeSnap[],
  snap: PrimeOptimizeSnap | null | undefined,
): number | undefined {
  if (!snap) return undefined;

  const exact = tracks.findIndex(
    (t) =>
      (t.languageCode ?? '') === (snap.languageCode ?? '')
      && Boolean(t.enhanced) === Boolean(snap.enhanced)
      && (t.optimizeSourceIndex ?? -1) === (snap.optimizeSourceIndex ?? -1),
  );
  if (exact >= 0) return exact;

  // Type 3 Optimized removed → plain same language (or optimizeSourceIndex).
  if (snap.enhanced) {
    if (snap.optimizeSourceIndex != null) {
      const src = tracks[snap.optimizeSourceIndex];
      if (src && !src.enhanced) return snap.optimizeSourceIndex;
    }
    if (snap.languageCode) {
      const plain = tracks.findIndex(
        (t) => !t.enhanced && t.languageCode === snap.languageCode,
      );
      if (plain >= 0) return plain;
    }
  }

  return undefined;
}

export function snapCourseraSubtitleRow(
  track: CourseraOptimizeSnap | null | undefined,
): CourseraOptimizeSnap | null {
  if (!track) return null;
  return {
    code: track.code,
    enhanced: track.enhanced,
    optimizeSourceCode: track.optimizeSourceCode,
  };
}

export function resolveCourseraIndexAfterOptimizeToggle(
  tracks: CourseraOptimizeSnap[],
  snap: CourseraOptimizeSnap | null | undefined,
): number | undefined {
  if (!snap) return undefined;

  const exact = tracks.findIndex(
    (t) =>
      (t.code ?? '') === (snap.code ?? '')
      && Boolean(t.enhanced) === Boolean(snap.enhanced),
  );
  if (exact >= 0) return exact;

  // Type 3 Optimized removed → plain same language.
  if (snap.enhanced) {
    const sourceCode = snap.optimizeSourceCode ?? snap.code;
    if (sourceCode) {
      const plain = tracks.findIndex(
        (t) => !t.enhanced && t.code === sourceCode,
      );
      if (plain >= 0) return plain;
    }
  }

  return undefined;
}
